(function (window) {
    'use strict';

    /**
     * THE GUARD CLAUSE
     * We stop execution if:
     * 1. The URL path contains '/wp-admin' (Backend protection)
     * 2. The element '#wpadminbar' exists (WordPress Admin Bar detection)
     */
    const isBackend = window.location.pathname.includes('/wp-admin') || 
                      window.location.pathname.includes('/wp-login.php');

    if (isBackend) {
        // Silent exit to prevent any interference with WordPress Admin functionality
        return;
    }

/* ────────────────────────────────────────────────────────────────────── */
/*  E_Measurement V5.2 – Brevo Transaction Saving + Bug Fixes          */
/* ────────────────────────────────────────────────────────────────────── */
class E_Measurement {
    #config;
    #storage = null;
    #cacheMaxAge = 30 * 86400000; // 30 days in milliseconds

    #affiliatePaths = ['/fora/', '/go/', '/ext/', '/out/', '/via/', '/uit/'];
    #productionDomain = 'emailtooltester.com';

    // PERFORMANCE: Cache DOM queries
    #adminBar = null; 

    constructor(config) {
        this.#config = config || {};
    }

    init() {
        if (!this.#validateConfig()) return;

        // Cache the Admin Bar element once (performance optimization)
        this.#adminBar = document.getElementById('wpadminbar'); 

        this.#processPageLoad();     
        this.#bindEvents();           
        this.#applyInitialRules();     
        this.#setupMutationObserver(); 

        // Pre-inject attribution fields into Brevo forms already in the DOM
        if (this.#config.formSelector) {
            document.querySelectorAll(this.#config.formSelector).forEach(form => {
                this.#preInjectFormFields(form);
            });
        }
    }

    #validateConfig() { return !!this.#config.siteOrigin; }
    
    #debugLog(message, data = '') {
        if (window.location.hostname !== this.#productionDomain) {
            console.log(`%c[ETT Debug] ${message}`, 'color: #007bff; font-weight: bold;', data);
        }
    }

    #processPageLoad() {
        const params = new URLSearchParams(window.location.search);
        const currentSource = params.get('utm_source');
        let storage = this.#getStorage() || this.#createNewStorage();
        let storageChanged = false;

        // ── EXTERNAL VISIT DETECTION ──────────────────────────────────────────
        // A visit is EXTERNAL when the visitor did NOT arrive from within our own
        // site: the URL carries external UTM params, or the referrer is an external
        // website. Rule: "first external signal encountered during the session wins"
        // (external persists) — once `is_external` is set it is never reset, and
        // internal UTM variables are gated off for the rest of the session.
        const externalUtm       = this.#hasExternalUtm();
        const externalReferrer  = Boolean(document.referrer) && !this.#isInternal(document.referrer);
        const isExternalVisit   = externalUtm || externalReferrer;

        if (isExternalVisit && !storage.is_external) {
            storage.is_external = true;
            storageChanged = true;

            // Reset stale internal attribution so it cannot leak into an external visit.
            storage.ett_campaign = '';
            storage.ett_medium   = '';
            storage.custom_internal_source = '';

            // Persist the external source/campaign/medium BEFORE the URL is cleaned,
            // so they survive for the whole session and into the Brevo transaction.
            if (currentSource) {
                if (!storage.first_touch) storage.first_touch = currentSource;
                storage.ett_campaign = this.#sanitizeValue(params.get('utm_campaign'));
                storage.ett_medium   = this.#sanitizeValue(params.get('utm_medium'));
                this.#pushUniqueLog(storage, {
                    source:   currentSource,
                    campaign: params.get('utm_campaign') || '',
                    medium:   params.get('utm_medium') || ''
                });
            }

            this.#debugLog('External visit recorded (external UTM priority)', {
                utm: externalUtm,
                referrer: externalReferrer,
                source: currentSource
            });
        } else if (currentSource && !storage.is_external) {
            // Internal session that happens to carry a UTM source: record it as-is.
            if (!storage.first_touch) { storage.first_touch = currentSource; storageChanged = true; }
            this.#pushUniqueLog(storage, { source: currentSource, campaign: '', medium: '' });
            storageChanged = true;
        }

        // Only set last_internal_touch on first visit or if unknown
        if (!storage.last_internal_touch || storage.last_internal_touch === 'unknown') {
            // OPTIMIZATION: Store shortened URL (strip query params for storage)
            try {
                const urlObj = new URL(window.location.href);
                // Keep origin + pathname only for storage (saves ~500-2000 chars)
                storage.last_internal_touch = urlObj.origin + urlObj.pathname;
            } catch(e) {
                storage.last_internal_touch = window.location.href.substring(0, 500);
            }
            storageChanged = true;
        }

        if (storageChanged) this.#setStorage(storage);

        this.#cleanInternalUrls();

        // Preserve the original (pre-clean) URL for Clicky campaign attribution.
        // Clicky's get_href() prefers clicky_custom.href, and clicky.init() runs at
        // window load — after this cleanup — so without this Clicky never sees the UTMs.
        // Clicky merges with any existing object (clicky_custom = clicky_custom || {}).
        if (window.location.search.length > 0) {
            window.clicky_custom = window.clicky_custom || {};
            if (!window.clicky_custom.href) {
                window.clicky_custom.href = window.location.pathname + window.location.search;
            }
        }

        // Clean URL after processing (preserves page appearance, removes UTMs)
        if (window.history && params.toString().length > 0) {
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }

    /**
     * True when the current page URL carries any external UTM query parameter.
     *
     * IMPORTANT: This must be checked during page load, BEFORE the URL is
     * cleaned by `history.replaceState` — after cleanup `location.search` is empty.
     *
     * @returns {boolean} true if any 'utm_*' param is present in the URL.
     */
    #hasExternalUtm() {
        const params = new URLSearchParams(window.location.search);
        for (const key of params.keys()) {
            if (key.startsWith('utm_')) return true;
        }
        return false;
    }

    #bindEvents() {
        // Use capture phase for click events (fires before default handlers)
        document.addEventListener('click', (e) => this.#handleInteraction(e), true);
        
        // Add form submission tracking - only if formSelector is configured
        if (this.#config.formSelector) {
            document.addEventListener('submit', (e) => this.#handleFormSubmission(e), true);
        }
    }

    #setupMutationObserver() {
        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.addedNodes.length === 0) continue;
                
                for (const node of mutation.addedNodes) {
                    if (node.nodeType !== Node.ELEMENT_NODE) continue;

                    // ✅ FIX: Re-query admin bar each time for safety
                    const currentAdminBar = document.getElementById('wpadminbar');
                    if (currentAdminBar && currentAdminBar.contains(node)) {
                        continue; // Skip admin bar nodes
                    }

                    if (node.tagName === 'A') {
                        this.#cleanLink(node);
                    } else if (node.querySelectorAll) {
                        // ✅ FIX: Filter out admin bar links from query results
                        node.querySelectorAll('a').forEach(a => {
                            if (!a.closest('#wpadminbar')) {
                                this.#cleanLink(a);
                            }
                        });

                        // Pre-inject attribution fields into any Brevo form added dynamically
                        if (this.#config.formSelector) {
                            const forms = node.matches && node.matches(this.#config.formSelector)
                                ? [node]
                                : node.querySelectorAll(this.#config.formSelector);
                            forms.forEach(form => this.#preInjectFormFields(form));
                        }
                    }
                }
            }
        });

        // OPTIMIZATION: Only observe body, not entire document
        observer.observe(document.body, { childList: true, subtree: true });
    }

    /**
     * PRE-INJECT attribution fields into a Brevo form as soon as it appears in the DOM.
     * This ensures the fields are populated before Brevo's own AJAX submit reads them.
     */
    #preInjectFormFields(form) {
        if (!form || form.dataset.ettPreInjected) return;
        form.dataset.ettPreInjected = '1';

        let storage = this.#getStorage();
        if (!storage) {
            storage = this.#createNewStorage();
            this.#setStorage(storage);
        }

        const params = new URLSearchParams(window.location.search);
        const currentUTM = params.get('utm_source');
        const historyString = this.#formatHistory(storage);

        const latestUTMValue = this.#latestSource(storage, currentUTM);

        const forceInject = (name, val, allowOverwrite = false) => {
            let input = form.querySelector(`[name="${name}"]`);
            if (!input) {
                input = document.createElement('input');
                input.type = 'hidden';
                input.name = name;
                form.appendChild(input);
            }

            const isNotEmpty = input.value && input.value.trim() !== '';
            if (allowOverwrite || !isNotEmpty) {
                let cleanValue = (val || '').toString().replace(/[<>'"]/g, '');
                cleanValue = cleanValue.substring(0, 500);
                input.value = cleanValue;
            }
        };

        const formPosition = this.#getFormPosition(form);

        forceInject('SIGNUPSOURCE', window.location.href, true);
        forceInject('SIGNUP_UTM_SOURCE', latestUTMValue, true);
        forceInject('SIGNUP_PREVIOUS_PAGE', storage.last_internal_touch, true);
        forceInject('SIGNUP_INTERNAL_LINK_PLACEMENT', this.#getInternalLinkPlacement(storage), true);
        forceInject('SIGNUP_UTM_SOURCE_HISTORY', historyString, true);
        forceInject('SIGNUP_FORM_POSITION', formPosition, true);
        forceInject('CUSTOM_SIGNUP_SOURCE', latestUTMValue, true);

        // Compute latest campaign/medium first, then inject
        const campaignLatest = this.#sanitizeValue(storage.ett_campaign);
        const mediumLatest   = this.#sanitizeValue(storage.ett_medium);

        forceInject('SIGNUP_UTM_CAMPAIGN', campaignLatest, true);
        forceInject('SIGNUP_UTM_MEDIUM', mediumLatest, true);
    }

    /**
     * IDENTIFY STRUCTURAL CONTEXT
     * Order matters: banner is checked BEFORE article because a banner can live inside an <article>
     */
    #getStructuralContext(anchor) {
        
        if (anchor.closest('#topHeader'))           return 'top_banner';
        if (anchor.closest('.global-header__nav'))  return 'main_menu';
        if (anchor.closest('.inline_banner'))       return 'inline_banner';
        
        if (anchor.closest('article')) {
            // Any image link inside article = inline_banner
            if (anchor.querySelector('img')) return 'inline_banner';
            return 'article_link';
        }

        //default
        return 'link';
    }

    /**
     * DETERMINES WHERE A FORM IS LOCATED
     * Returns: 'footer' | 'landing_page' | 'inline'
     * 
     * SEARCH ORDER: footer → landing_page → inline (default)
     * Footer is checked first because it has the most specific selectors
     */
    #getFormPosition(form) {
       
        
        // ── 1. LANDING PAGE DETECTION ─────────────────────
        // Landing page forms are inside .landing-ebook or .page-header--ebook container
        if (form.closest('.landing-ebook, .page-header--ebook')) return 'landing_page';

        // ── 2. FOOTER DETECTION (check first - most specific) ──
        // Checks selectors from footer.php and partials/global/footer.php
        if (form.closest('footer, .global-footer, .global-footer__newsletter, #footer-newsletter')) return 'footer';

        // ── 3. INLINE (default) ───────────────────────────
        // Form is inside main content of a regular page/post (blog, article, etc.)
        return 'inline';

    }

    /**
     * Resolves the internal link placement attribution value.
     *
     * Returns the structural context of the last internal link the visitor
     * clicked, or 'undetected' when no internal link interaction was recorded
     * (e.g., direct visit, bookmark, or external referrer).
     *
     * @param {Object|null} storage - The ETT tracking storage object.
     * @returns {string} The placement value ('top_banner', 'main_menu',
     *                   'inline_banner', 'article_link', 'link', or 'undetected').
     */
    #getInternalLinkPlacement(storage) {
        return storage?.custom_internal_source || 'undetected';
    }

    #handleInteraction(event) {
        const anchor = event.target.closest('a');
        if (!anchor || !anchor.href) return;
        
        // ✅ FIX: Use closest() directly - always reliable, even if admin bar added dynamically
        if (anchor.closest('#wpadminbar')) {
            return; // Skip admin bar clicks - CRITICAL FOR CLICK TRACKING
        }

        if (anchor.classList.contains('no-blank')) return;

        // Capture internal UTM attribution (latest + unified history log).
        // Internal UTM variables come from `data-ett-campaign`/`data-ett-medium`
        // `data-ett-source` on internal links (ETT Link Cleaner, header banner,
        // inline banners). A value of "-" means "empty" (banner convention).
        // INTERNAL UTM PRIORITY: internal UTM variables are tracked ONLY when the
        // session is not external. Once an external signal is seen (is_external),
        // external attribution wins and internal values are never recorded.
        const ettCampaign = this.#normalizeInternalUtm(anchor.dataset.ettCampaign);
        const ettMedium   = this.#normalizeInternalUtm(anchor.dataset.ettMedium);
        const ettSource   = this.#normalizeInternalUtm(anchor.dataset.ettSource);
        if (ettCampaign || ettMedium || ettSource) {
            const s = this.#getStorage() || this.#createNewStorage();
            if (!s.is_external) {
                const params = new URLSearchParams(window.location.search);
                const source = ettSource || this.#latestSource(s, params.get('utm_source'));
                if (ettCampaign) s.ett_campaign = ettCampaign;
                if (ettMedium)   s.ett_medium   = ettMedium;
                this.#pushUniqueLog(s, { source, campaign: ettCampaign || '', medium: ettMedium || '' });
                this.#setStorage(s);
            }
        }

        const href = anchor.href;
        const isInternal = this.#isInternal(href);

        if (isInternal) {
            const storage = this.#getStorage();
            if (storage) {
                // Store full URL at click time for accurate attribution (limited to 600 chars)
                try {
                    storage.last_internal_touch = window.location.href.substring(0, 600);
                } catch(e) {
                    storage.last_internal_touch = window.location.href.substring(0, 600);
                }

                // COMBINARE: Context structural + URL complet cu text-fragments
                const context = this.#getStructuralContext(anchor);
                // Formatul final: "context | URL"
                storage.custom_internal_source = `${context}`;
                this.#setStorage(storage);
            }

            // Handle affiliate link attributes (SEO + Security)
            const isAffiliate = this.#affiliatePaths.some(p => href.includes(p));
            if (isAffiliate) {
                anchor.rel = this.#mergeRel(anchor.rel, ['noopener', 'noreferrer', 'nofollow']);
                if (anchor.target !== '_blank') anchor.target = '_blank';
            }
        } else if (href.startsWith('http')) {
            this.#trackExternalClick(href);
        }
        
        this.#handleBrevoAffiliateTracking(anchor, href);
    }

    #isInternal(url) {
        try {
            const urlObj = new URL(url);
            const configOrigin = new URL(this.#config.siteOrigin);
            return urlObj.hostname === configOrigin.hostname;
        } catch(e) { return false; }
    }

    #applyInitialRules() {
        // OPTIMIZATION: Use requestIdleCallback for non-critical DOM manipulation
        const runScan = () => {
            document.querySelectorAll('a[href]').forEach(link => {
                // ✅ FIX: Filter out admin bar links during initial scan
                if (!link.closest('#wpadminbar')) {
                    this.#cleanLink(link);
                }
            });
        };

        if (typeof window.requestIdleCallback !== 'undefined') {
            window.requestIdleCallback(runScan);
        } else {
            runScan(); // Fallback for older browsers
        }
    }

    #cleanLink(link) {
        // never misses admin bar links
        if (link.closest('#wpadminbar')) {
            return; // NEVER process admin bar links - preserves UTMs for Clicky tracking
        }

        const href = link.href;
        if (!href) return;

        // Skip non-HTTP/HTTPS links (mailto:, tel:, etc.)
        if (!href.startsWith('http')) return;

        // === EXTERNAL HTTP LINK: set rel + target at LOAD time (SEO/security) =
        if (!this.#isInternal(href)) {
            link.rel = this.#mergeRel(link.rel, ['noopener', 'noreferrer']);
            if (!link.classList.contains('no-blank')) link.target = '_blank';
        }

        // === INTERNAL LINK: strip UTM query params =============================
        try {
            const urlObj = new URL(href, this.#config.siteOrigin);
            // Remove query params but preserve hash anchor
            if (urlObj.search.length > 0) {
                link.href = urlObj.origin + urlObj.pathname + urlObj.hash;
            }
        } catch(e) {}

        // === AFFILIATE LINK: apply rel + target at LOAD time (crawlers see it) =
        // Use original href attribute before query-param stripping
        const hrefAttr = link.getAttribute('href');
        if (this.#affiliatePaths.some(p => hrefAttr && hrefAttr.includes(p))) {
            link.rel = this.#mergeRel(link.rel, ['noopener', 'noreferrer', 'nofollow']);
            if (!link.classList.contains('no-blank')) link.target = '_blank';

            // Cache keyword at scan time for efficient click tracking
            if (!link.dataset.kwd) {
                const kw = this.#extractAffiliateKeyword(hrefAttr);
                if (kw) link.dataset.kwd = kw;
            }
        }
    }

    #cleanInternalUrls() { 
        document.querySelectorAll('a[href]').forEach(link => this.#cleanLink(link)); 
    }

    #trackExternalClick(href) {
        if (typeof window.gtag === 'function') {
            const pageType = this.#config.pageType || 'general';
            window.gtag('event', `external_${pageType}`, { 
                'event_category': 'engagement', 
                'event_label': href.substring(0, 200) // Limit label size for GA
            });
        }
    }

    #mergeRel(currentRel, newValues) {
        if (!currentRel) return newValues.join(' ');
        const existing = currentRel.split(/\s+/);
        return Array.from(new Set([...existing, ...newValues])).join(' ');
    }

    /**
     * Extract affiliate keyword from href attribute.
     * Used at scan time for caching in dataset.affKeyword (efficient click tracking).
     */
    #extractAffiliateKeyword(hrefAttr) {
        try {
            const urlObj = new URL(hrefAttr, this.#config.siteOrigin);
            for (const p of this.#affiliatePaths) {
                const match = urlObj.pathname.match(new RegExp(`${p}(\\w+)`));
                if (match) return match[1];
            }
        } catch(e) {}
        return null;
    }

    #handleBrevoAffiliateTracking(anchor, href) {
        if (typeof window.Brevo === 'undefined') return;
        
        const keyword = anchor.dataset.kwd || null;
        
        if (keyword) {
            window.Brevo.push(['track', `aff_click_${keyword}`, { affiliate_link: href }]);
        } else if (!anchor.rel.includes('nofollow')) {
            window.Brevo.push(['track', 'aff_click', { affiliate_link: href }]);
        }
    }

    /**
     * READS cookie, validates age AND size. If over limit, keeps only latest 3 history entries.
     */
    #getStorage() {
        try {
            const cookieName = this.#config.cookieName || 'ett_data';
            const raw = document.cookie.split('; ').find(row => row.startsWith(cookieName + '='));
            if (!raw) return null;
            
            const parsed = JSON.parse(decodeURIComponent(raw.split('=')[1]));
            
            // Validate age - delete expired cookies
            if (!parsed.created_at || Date.now() - parsed.created_at > this.#cacheMaxAge) {
                return null;
            }
            
            // SIZE PROTECTION: If cookie over limit, keep only latest 3 history entries
            const currentSize = new TextEncoder().encode(raw).length;
            if (currentSize > this.#config.cookieMaxSize) {
                // Keep only the latest 3 history entries (most recent)
                if (Array.isArray(parsed.history) && parsed.history.length > 3) {
                    // slice(-3) gets the LAST 3 items from array (most recent)
                    parsed.history = parsed.history.slice(-3);
                } else if (parsed.history.length > 0) {
                    // If history has only 1-3 items but still over limit, delete it entirely
                    parsed.history = [];
                }
                this.#debugLog(`Cookie over limit (${currentSize} bytes) - reduced history to 3 entries`);
            }
            
            return parsed;
        } catch(e) { 
            this.#debugLog('Error parsing storage', e);
            return null; 
        }
    }

    /**
     * WRITES cookie with size validation. If over limit, trims history to latest 3 entries.
     */
    #setStorage(data) {
        try {
            const cookieName = this.#config.cookieName || 'ett_data';
            const expiryDate = new Date(Date.now() + this.#cacheMaxAge).toUTCString();
            
            // Create cookie string and check size BEFORE setting
            const jsonString = JSON.stringify(data);
            const encodedData = encodeURIComponent(jsonString);
            const cookieValue = `${cookieName}=${encodedData};expires=${expiryDate};path=/;SameSite=Lax`;
            const cookieSize = new TextEncoder().encode(cookieValue).length;

            // SIZE PROTECTION: If cookie would exceed limit, trim history to latest 3
            if (cookieSize > this.#config.cookieMaxSize) {
                // First attempt: Keep only latest 3 history entries
                if (Array.isArray(data.history) && data.history.length > 0) {
                    data.history = data.history.slice(-3); // Keep last 3
                    
                    // Re-check size after trimming history
                    const newJsonString = JSON.stringify(data);
                    const newEncodedData = encodeURIComponent(newJsonString);
                    const newCookieValue = `${cookieName}=${newEncodedData};expires=${expiryDate};path=/;SameSite=Lax`;
                    const newCookieSize = new TextEncoder().encode(newCookieValue).length;
                    
                    if (newCookieSize <= this.#config.cookieMaxSize) {
                        // Success! History trimmed to 3 was enough
                        document.cookie = newCookieValue;
                        this.#debugLog(`Cookie saved with 3 history entries (${newCookieSize} bytes)`);
                        return;
                    } else {
                        // Still too large, delete history completely and try again
                        data.history = [];
                        this.#debugLog(`History reduced to 3 still too large (${newCookieSize} bytes), deleted history`);
                        
                        // Final attempt: try without history at all
                        const finalJsonString = JSON.stringify(data);
                        const finalEncodedData = encodeURIComponent(finalJsonString);
                        document.cookie = `${cookieName}=${finalEncodedData};expires=${expiryDate};path=/;SameSite=Lax`;
                        return;
                    }
                }
                
                this.#debugLog(`Cookie size warning: ${cookieSize} bytes (limit: ${this.#config.cookieMaxSize})`);
            }

            document.cookie = cookieValue;
        } catch(e) { 
            this.#debugLog('Failed to save storage', e); 
        }
    }

    #createNewStorage() {
        return {
            first_touch: null,
            history: [],
            ett_campaign: null,
            ett_medium: null,
            is_external: false,
            last_internal_touch: null,
            custom_internal_source: null,
            created_at: Date.now()
        };
    }

    /**
     * Normalize an INTERNAL UTM value read from a link's data attributes.
     * Treats "" and "-" (banner convention for "no value") as empty, and
     * applies the standard sanitization for everything else.
     *
     * @returns {string} sanitized value, or '' when null/undefined/empty/'-'.
     */
    #normalizeInternalUtm(value) {
        if (value === null || value === undefined) return '';
        const sanitized = this.#sanitizeValue(value);
        return (sanitized === '' || sanitized === '-') ? '' : sanitized;
    }

    /**
     * Sanitize a single attribution value.
     * Strips HTML/JS chars, clamps to 500 chars, returns '' when empty.
     */
    #sanitizeValue(value) {
        if (value === null || value === undefined) return '';
        return value.toString().replace(/[<>'"]/g, '').substring(0, 500);
    }

    /**
     * Sanitize a value for inclusion in the unified history string.
     * Also strips commas/pipes so the '|' (log) and ',' (field) delimiters
     * can never be broken by a raw value.
     */
    #historyValue(value) {
        return this.#sanitizeValue(value).replace(/[|,]/g, '');
    }

    /**
     * Build one readable history log from a { source, campaign, medium } object.
     * Comma-separated fields; campaign labeled when source is missing,
     * medium labeled when source OR campaign is missing.
     */
    #formatHistoryLog(log) {
        const src  = this.#historyValue(log && log.source);
        const camp = this.#historyValue(log && log.campaign);
        const med  = this.#historyValue(log && log.medium);
        const parts = [];

        if (src)  parts.push(src);
        if (camp) parts.push((src ? '' : 'campaign: ') + camp);
        if (med)  parts.push(((src && camp) ? '' : 'medium: ') + med);

        return parts.join(', ');
    }

    /**
     * Build the SINGLE unified history string from storage.history.
     * Each log (source+campaign+medium) is separated by '|'.
     */
    #formatHistory(storage) {
        const logs = Array.isArray(storage && storage.history) ? storage.history : [];
        return logs.map(log => this.#formatHistoryLog(log)).filter(Boolean).join('|');
    }

    /**
     * Append a unique history log (by its formatted string), capped at maxHistorySize.
     */
    #pushUniqueLog(storage, log) {
        if (!storage || !log) return;
        if (!Array.isArray(storage.history)) storage.history = [];

        const formatted = this.#formatHistoryLog(log);
        if (!formatted) return;

        if (!storage.history.some(existing => this.#formatHistoryLog(existing) === formatted)) {
            storage.history.push(log);
            const maxHistory = this.#config.maxHistorySize || 10;
            if (storage.history.length > maxHistory) {
                storage.history = storage.history.slice(-maxHistory);
            }
        }
    }

    /**
     * Resolve the latest UTM source: current URL value first, else last history log's source.
     */
    #latestSource(storage, currentUTM) {
        if (currentUTM) return currentUTM;
        const logs = Array.isArray(storage && storage.history) ? storage.history : [];
        const last = logs[logs.length - 1];
        return (last && last.source) ? last.source : '';
    }

    /**
     * BREVO TRANSACTION API
     * Creates/updates a Brevo contact with all attribution fields.
     * Calls a WordPress REST API proxy so credentials never reach the browser.
     * Runs asynchronously and never blocks the original form submission.
     */
    async #saveTransactionToBrevo(form, storage, formPosition) {

        // CAPTCHA GATE (defense-in-depth): never send a transaction while the
        // captcha response is still empty.
        if (this.#isCaptchaPending(form)) {
            this.#debugLog("Brevo transaction skipped: captcha not solved");
            return;
        }
        if (!this.#config.brevoRestUrl || !this.#config.brevoRestNonce) {
            this.#debugLog('Brevo transaction skipped: missing REST endpoint config');
            return;
        }

        const emailInput = form.querySelector('input[type="email"], input[name="EMAIL"], input#EMAIL');
        const email = emailInput ? emailInput.value.trim() : '';

        if (!email) {
            this.#debugLog('Brevo transaction skipped: no email found in form');
            return;
        }

        const params = new URLSearchParams(window.location.search);
        const currentUTM = params.get('utm_source');
        const historyString = this.#formatHistory(storage);

        const latestUTMValue = this.#latestSource(storage, currentUTM);

        // Resolve latest campaign/medium attribution values (compute first)
        const campaignLatest = this.#sanitizeValue(storage.ett_campaign);
        const mediumLatest   = this.#sanitizeValue(storage.ett_medium);

        const signupSource = form.querySelector('input[name="CUSTOM_SIGNUP_SOURCE"]')?.value ||
                             form.querySelector('input[name="SIGNUPSOURCE"]')?.value ||
                             latestUTMValue ||
                             'organic';

        // Determine form ID
        let formId = 'newsletter_inline';
        if (form.id) {
            formId = form.id;
        } else if (form.closest('footer, .global-footer__newsletter')) {
            formId = 'newsletter_footer';
        } else if (form.closest('.landing')) {
            formId = 'newsletter_landing';
        }

        // Collect all form data for logging
        const formData = {
            form_id: formId,
            form_position: formPosition,
            page_url: window.location.href,
            SIGNUPSOURCE: form.querySelector('input[name="SIGNUPSOURCE"]')?.value || '',
            SIGNUP_UTM_SOURCE: form.querySelector('input[name="SIGNUP_UTM_SOURCE"]')?.value || '',
            SIGNUP_PREVIOUS_PAGE: form.querySelector('input[name="SIGNUP_PREVIOUS_PAGE"]')?.value || '',
            SIGNUP_INTERNAL_LINK_PLACEMENT: form.querySelector('input[name="SIGNUP_INTERNAL_LINK_PLACEMENT"]')?.value || '',
            SIGNUP_UTM_SOURCE_HISTORY: form.querySelector('input[name="SIGNUP_UTM_SOURCE_HISTORY"]')?.value || '',
            SIGNUP_FORM_POSITION: form.querySelector('input[name="SIGNUP_FORM_POSITION"]')?.value || '',
            CUSTOM_SIGNUP_SOURCE: form.querySelector('input[name="CUSTOM_SIGNUP_SOURCE"]')?.value || '',
            SIGNUP_UTM_CAMPAIGN: form.querySelector('input[name="SIGNUP_UTM_CAMPAIGN"]')?.value || '',
            SIGNUP_UTM_MEDIUM: form.querySelector('input[name="SIGNUP_UTM_MEDIUM"]')?.value || '',
            utm_source: currentUTM || '',
            utm_campaign: params.get('utm_campaign') || '',
            utm_medium: params.get('utm_medium') || '',
            utm_content: params.get('utm_content') || '',
            utm_term: params.get('utm_term') || '',
            referrer: document.referrer || '',
            user_agent: navigator.userAgent,
            screen_resolution: `${window.screen.width}x${window.screen.height}`,
            viewport_size: `${window.innerWidth}x${window.innerHeight}`,
            timestamp: new Date().toISOString()
        };

        const transactionData = {
            email: email,
            attributes: {
                TX_SIGNUP_DATE: new Intl.DateTimeFormat('en-CA', {
                    timeZone: 'Europe/Madrid',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false
                }).format(new Date()),
                TX_SIGNUP_SOURCE: signupSource,
                TX_SIGNUP_UTM_SOURCE: latestUTMValue,
                TX_SIGNUP_UTM_CAMPAIGN: campaignLatest,
                TX_SIGNUP_UTM_MEDIUM: mediumLatest,
                TX_SIGNUP_FORM_POSITION: formPosition,
                TX_SIGNUP_INTERNAL_LINK_PLACEMENT: this.#getInternalLinkPlacement(storage),
                TX_SIGNUP_PREVIOUS_PAGE: storage.last_internal_touch || '',
                TX_SIGNUP_UTM_SOURCE_HISTORY: historyString,
                // Regular (non-transactional) contact attributes: update them in the
                // same Brevo call. Only sent when a real value exists so we never
                // overwrite the contact field with an empty placeholder.
                ...(campaignLatest ? { SIGNUP_UTM_CAMPAIGN: campaignLatest } : {}),
                ...(mediumLatest   ? { SIGNUP_UTM_MEDIUM:   mediumLatest }   : {})
            },
            form_data: formData
        };

        try {
            const response = await fetch(this.#config.brevoRestUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': this.#config.brevoRestNonce
                },
                body: JSON.stringify(transactionData),
                keepalive: true
            });

            if (!response.ok) {
                const errorText = await response.text().catch(() => 'unknown error');
                this.#debugLog(`Brevo transaction API error ${response.status}:`, errorText);
                return;
            }

            const result = await response.json().catch(() => ({}));
            this.#debugLog('Brevo transaction saved:', result);

            // Store returned contact ID in hidden field if available
            if (result && result.id) {
                let txInput = form.querySelector('input[name="BREVO_TRANSACTION_ID"]');
                if (!txInput) {
                    txInput = document.createElement('input');
                    txInput.type = 'hidden';
                    txInput.name = 'BREVO_TRANSACTION_ID';
                    form.appendChild(txInput);
                }
                txInput.value = result.id;
            }
        } catch (err) {
            this.#debugLog('Brevo transaction fetch failed:', err);
        }
    }

    /**
     * FORM SUBMISSION HANDLER - injects attribution data into Brevo forms
     */
    /**
     * True when the form reCAPTCHA has not been solved yet (empty g-recaptcha-response).
     * Submissions Brevo rejects at the captcha step must not fire tracking events
     * or create Brevo transactions.
     */
    #isCaptchaPending(form) {
        const response = form.querySelector("[name=g-recaptcha-response], .g-recaptcha-response");
        return !!(response && !response.value);
    }

    #handleFormSubmission(event) {
        const form = event.target.closest(this.#config.formSelector);
        if (!form) return;

        // CAPTCHA GATE: this capture-phase handler runs before Brevo validates the
        // captcha. If it is unsolved the submission will be rejected, so block it
        // here: no tracking events and no Brevo transaction.
        if (this.#isCaptchaPending(form)) {
            event.preventDefault();
            console.log("Please check the recaptcha");
            return;
        }

        // Ensure storage exists so the handler always runs, even on first visit.
        let storage = this.#getStorage();
        if (!storage) {
            storage = this.#createNewStorage();
            this.#setStorage(storage);
        }

        const formPosition = this.#getFormPosition(form);

        // Secure field injection with size limits
        const forceInject = (name, val, allowOverwrite = false) => {
            let input = form.querySelector(`[name="${name}"]`);
            if (!input) {
                input = document.createElement('input');
                input.type = 'hidden';
                input.name = name;
                form.appendChild(input);
            }

            const isNotEmpty = input.value && input.value.trim() !== '';
            if (allowOverwrite || !isNotEmpty) {
                // Sanitize: remove HTML/JS characters AND limit to 500 chars per field
                let cleanValue = (val || '').toString().replace(/[<>'"]/g, '');
                cleanValue = cleanValue.substring(0, 500); // Limit each field to 500 chars
                input.value = cleanValue;
            }
        };

        const params = new URLSearchParams(window.location.search);
        const currentUTM = params.get('utm_source');
        const historyString = this.#formatHistory(storage);

        // Determine the most recent UTM source
        const latestUTMValue = this.#latestSource(storage, currentUTM);

        // Inject attribution data into form fields (sent to Brevo on submit)
        forceInject('SIGNUPSOURCE', window.location.href, true); 
        forceInject('SIGNUP_UTM_SOURCE', latestUTMValue, true); 
        forceInject('SIGNUP_PREVIOUS_PAGE', storage.last_internal_touch, true);
        forceInject('SIGNUP_INTERNAL_LINK_PLACEMENT', this.#getInternalLinkPlacement(storage), true);
        forceInject('SIGNUP_UTM_SOURCE_HISTORY', historyString, true);
        forceInject('SIGNUP_FORM_POSITION', formPosition, true);
        forceInject('CUSTOM_SIGNUP_SOURCE', latestUTMValue, true);

        // Compute latest campaign/medium first, then inject
        const campaignLatest = this.#sanitizeValue(storage.ett_campaign);
        const mediumLatest   = this.#sanitizeValue(storage.ett_medium);

        forceInject('SIGNUP_UTM_CAMPAIGN', campaignLatest, true);
        forceInject('SIGNUP_UTM_MEDIUM', mediumLatest, true);

        // Determine form ID safely (fixed operator precedence)
        let formId = 'newsletter_inline';
        if (form.id) {
            formId = form.id;
        } else if (form.closest('footer, .global-footer__newsletter')) {
            formId = 'newsletter_footer';
        } else if (form.closest('.landing')) {
            formId = 'newsletter_landing';
        }

        // Brevo event tracking for individual form submissions
        if (typeof window.Brevo !== 'undefined' && Array.isArray(window.Brevo)) {
            window.Brevo.push(['track', 'form_submitted', {
                'form_id': formId,
                'page_url': window.location.href.substring(0, 500),
                'form_position': formPosition,
                'utm_source': currentUTM || 'organic',
                'utm_campaign': params.get('utm_campaign') || 'none',
                'internal_source': storage.custom_internal_source || 'unknown'
            }]);
        }

        // Fire Google Analytics event for form submission
        if (typeof window.gtag === 'function') {
            window.gtag('event', 'brevo_form_submit', {
                source_history: historyString,
                last_touch: storage.last_internal_touch,
                internal_element: storage.custom_internal_source,
                form_position: formPosition
            });
        }

        // Save transaction to Brevo via REST API proxy (async, non-blocking).
        // Defer to next tick so the native form submission starts first;
        // keepalive ensures the request completes even if the page unloads.
        setTimeout(() => {
            this.#saveTransactionToBrevo(form, storage, formPosition);
        }, 0);
    }

}

// Initialize singleton with config from PHP (wp_localize_script creates 'ettConfig' global)
if (typeof ettConfig !== 'undefined') {
    window.EMeasurement = new E_Measurement(ettConfig);
}

})(window);
